export interface OneEuroOptions {
  /** Cutoff frequency at rest (Hz). Lower = more jitter smoothing when the signal is still. */
  minCutoff?: number;
  /** Speed coefficient. Higher = the cutoff rises faster with speed, cutting lag on fast motion. */
  beta?: number;
  /** Cutoff for the internal speed estimate. Rarely tuned. */
  dCutoff?: number;
}

/** Exponential smoothing: blend `value` toward `prev` by `alpha` ∈ [0,1]. */
const lerp = (alpha: number, value: number, prev: number): number =>
  alpha * value + (1 - alpha) * prev;

/** Smoothing factor for a given cutoff frequency and timestep (the filter's α). */
const smoothingFactor = (cutoff: number, dt: number): number => {
  const tau = 1 / (2 * Math.PI * cutoff);
  return 1 / (1 + tau / dt);
};

/**
 * Adaptive low-pass (1€) filter — one instance per scalar channel.
 * At low speed the cutoff is low (kills jitter when holding still); the cutoff rises with
 * speed to cut lag when panning. Less lag-for-jitter than Kalman / moving-average.
 * Ref: Casiez, Roussel, Vogel — CHI 2012.
 */
export class OneEuroFilter {
  private readonly minCutoff: number;
  private readonly beta: number;
  private readonly dCutoff: number;
  private valuePrev: number | null = null;
  private derivativePrev = 0;

  constructor(options: OneEuroOptions = {}) {
    this.minCutoff = options.minCutoff ?? 1;
    this.beta = options.beta ?? 0;
    this.dCutoff = options.dCutoff ?? 1;
  }

  /** Filter one sample. `dt` is the elapsed time since the previous sample, in seconds (> 0). */
  filter(value: number, dt: number): number {
    if (!(dt > 0)) throw new Error(`OneEuroFilter: dt must be positive, got ${dt}`);

    if (this.valuePrev === null) {
      this.valuePrev = value;
      return value;
    }

    const derivative = (value - this.valuePrev) / dt;
    const smoothedDerivative = lerp(smoothingFactor(this.dCutoff, dt), derivative, this.derivativePrev);

    const cutoff = this.minCutoff + this.beta * Math.abs(smoothedDerivative);
    const smoothedValue = lerp(smoothingFactor(cutoff, dt), value, this.valuePrev);

    this.valuePrev = smoothedValue;
    this.derivativePrev = smoothedDerivative;
    return smoothedValue;
  }

  /** Drop history so the next sample passes through unchanged. */
  reset(): void {
    this.valuePrev = null;
    this.derivativePrev = 0;
  }
}
